/** Admin: discount coupons for course checkout. The server applies them; this screen creates and switches them off. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate, fromIstInput } from '../../core/format.js';
import { reload } from '../../app/shell.js';
import { empty, pageHead, table, tag } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { closeModal, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

const coupons = {
  path: '/admin/coupons',
  roles: ['admin'],
  title: 'Coupons',
  load: (ctx) => api.get('/admin/coupons', { signal: ctx.signal }),
  view({ coupons: list }) {
    const head = html`<button type="button" class="btn" data-act="coupon.new">New coupon</button>`;
    return html`${pageHead('Coupons', 'A coupon takes a percentage off the course price before GST is added.', head)}
      ${list.length
        ? table(['Code', 'Discount', 'Used', 'Expires', 'Status', ''], list.map((c) => [
          html`<b>${c.code}</b>`, `${c.percent_off}%`, c.max_uses ? `${c.used_count} of ${c.max_uses}` : `${c.used_count}`,
          c.expires_at ? fmtDate(c.expires_at) : 'Never', c.active ? tag('Active', 'ok') : tag('Off', 'n'),
          html`<button type="button" class="btn ghost sm" data-act="coupon.toggle" data-id="${c.id}" data-on="${!c.active}">${c.active ? 'Switch off' : 'Switch on'}</button>`,
        ]))
        : empty('rupee', 'No coupons yet', 'Create one to offer a launch discount or a scholarship.', head)}`;
  },
};

register({
  'coupon.new': () => openModal({
    title: 'New coupon',
    body: html`<form id="coupon-form" data-submit="coupon.create" novalidate>
      ${field('cp-code', 'Code', { name: 'code', minlen: 3, maxlength: 30, pattern: '^[A-Za-z0-9_-]+$', help: 'Letters, numbers, dash or underscore. Students are not case-sensitive.', err: 'Use 3 to 30 letters, numbers, dashes or underscores.' })}
      <div class="f2">${field('cp-pct', 'Percent off', { name: 'percent_off', type: 'number', min: 1, max: 100, inputmode: 'numeric', err: 'Enter a whole number from 1 to 100.' })}
        ${field('cp-max', 'Most uses', { name: 'max_uses', type: 'number', min: 1, inputmode: 'numeric', req: false, help: 'Leave empty for no limit.' })}</div>
      ${field('cp-exp', 'Expires (India time)', { name: 'expires_at', type: 'datetime-local', req: false, help: 'Leave empty if it never expires.' })}
    </form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="coupon-form" class="btn">Create coupon</button>`,
  }),
  'coupon.create': (form) => submitForm(form, async ({ code, percent_off: pct, max_uses: max, expires_at: exp }) => {
    const body = { code: code.toUpperCase(), percent_off: Number(pct) };
    if (max) body.max_uses = Number(max);
    if (exp) body.expires_at = fromIstInput(exp);
    await api.post('/admin/coupons', { body });
    closeModal(false);
    toast('Coupon created', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
  'coupon.toggle': async (el) => {
    await api.patch(`/admin/coupons/${el.dataset.id}`, { body: { active: el.dataset.on === 'true' } });
    toast('Coupon updated', 'check');
    await reload();
  },
});

export const routes = [coupons];
